import { useEffect, useState, useCallback, useRef } from 'react';
import { amazonAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Save, Send, Sparkles, CheckCircle2, XCircle, AlertCircle,
  Eye, Copy, Check, ShoppingCart, ImageIcon, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const byteLen = (s) => new TextEncoder().encode(s || '').length;

const PUSH_STATUS_BADGE = {
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  done_with_errors: 'bg-amber-50 text-amber-800 border-amber-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

export default function AmazonListingPanel({ product }) {
  const productId = product.id;
  const [status, setStatus] = useState(null);
  const [pack, setPack] = useState(null);
  const [variations, setVariations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateResults, setValidateResults] = useState(null);
  const [previewJson, setPreviewJson] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [showMatrix, setShowMatrix] = useState(true);
  const dirtyRef = useRef(false);

  const load = useCallback(async ({ preserveEdits = false } = {}) => {
    try {
      const [s, p, v] = await Promise.all([
        amazonAPI.status(),
        amazonAPI.get(productId),
        amazonAPI.variations(productId),
      ]);
      setStatus(s.data);
      setVariations(v.data);
      setPack((prev) => {
        if (preserveEdits && prev && dirtyRef.current) {
          // Keep the founder's unsaved edits; only refresh job states
          return { ...prev, generation: p.data.generation, push: p.data.push };
        }
        return p.data;
      });
    } catch (err) {
      console.error('Amazon load failed', err);
      toast.error('Failed to load Amazon panel');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const genRunning = pack?.generation?.status === 'running';
  const pushRunning = pack?.push?.status === 'running';

  // Poll while AI generation or a push is in flight
  useEffect(() => {
    if (!genRunning && !pushRunning) return undefined;
    const handle = setInterval(async () => {
      try {
        const p = await amazonAPI.get(productId);
        setPack((prev) => {
          const wasRunning = prev?.generation?.status === 'running';
          const nowDone = p.data.generation?.status === 'done';
          if (wasRunning && nowDone) {
            dirtyRef.current = false;
            toast.success('Amazon listing generated');
            return p.data;
          }
          if (wasRunning && p.data.generation?.status === 'error') {
            toast.error(`Generation failed: ${p.data.generation.error || 'unknown error'}`);
          }
          if (prev && dirtyRef.current) {
            return { ...prev, generation: p.data.generation, push: p.data.push };
          }
          return p.data;
        });
      } catch { /* transient — next tick retries */ }
    }, 4000);
    return () => clearInterval(handle);
  }, [genRunning, pushRunning, productId]);

  const setField = (key, val) => {
    dirtyRef.current = true;
    setPack((prev) => ({ ...(prev || {}), [key]: val }));
  };
  const setBullet = (idx, val) => {
    dirtyRef.current = true;
    setPack((prev) => {
      const bullets = [...(prev.bullet_points || ['', '', '', '', ''])];
      bullets[idx] = val;
      return { ...prev, bullet_points: bullets };
    });
  };
  const setSpec = (key, val) => {
    dirtyRef.current = true;
    setPack((prev) => ({ ...prev, specifications: { ...(prev.specifications || {}), [key]: val } }));
  };
  const removeSpec = (key) => {
    dirtyRef.current = true;
    setPack((prev) => {
      const specs = { ...(prev.specifications || {}) };
      delete specs[key];
      return { ...prev, specifications: specs };
    });
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      await amazonAPI.generate(productId);
      setPack((prev) => ({ ...prev, generation: { status: 'running' } }));
      toast.success('Generating Amazon listing with AI — takes ~1 minute');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed to start');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!pack) return;
    try {
      setSaving(true);
      const editable = {
        title: pack.title,
        bullet_points: pack.bullet_points,
        description: pack.description,
        search_terms: pack.search_terms,
        specifications: pack.specifications,
        sku_base: pack.sku_base,
        brand: pack.brand,
        quantity: Number(pack.quantity) || 1,
        ring_size: pack.ring_size,
        lead_time_days: Number(pack.lead_time_days) || 14,
        amazon_product_type: pack.amazon_product_type,
        item_type_keyword: pack.item_type_keyword,
        image_urls: Array.isArray(pack.image_urls) ? pack.image_urls : [],
        resizable: !!pack.resizable,
        gtin_exemption: !!pack.gtin_exemption,
      };
      const res = await amazonAPI.patch(productId, editable);
      dirtyRef.current = false;
      setPack(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const overrides = {
        image_urls: Array.isArray(pack.image_urls) ? pack.image_urls : [],
        image_base: (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''),
      };
      const res = await amazonAPI.preview(productId, overrides);
      setPreviewJson(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      const overrides = {
        image_urls: Array.isArray(pack.image_urls) ? pack.image_urls : [],
        image_base: (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''),
      };
      const res = await amazonAPI.validate(productId, overrides);
      setValidateResults(res.data);
      const bad = res.data.results.filter((r) => r.status !== 'ACCEPTED' && r.status !== 'VALID').length;
      if (bad === 0) toast.success('Amazon accepted all sample payloads — ready to push');
      else toast.warning(`Amazon flagged ${bad} of ${res.data.results.length} sample SKUs — see issues below`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handlePublish = async () => {
    if (!(pack.title || '').trim()) {
      toast.error('Generate or write a title first');
      return;
    }
    if (!Array.isArray(pack.image_urls) || pack.image_urls.length === 0) {
      toast.error('Pick at least one photo — Amazon requires a main image.');
      return;
    }
    const count = (variations?.count || 0) + 1;
    if (!confirm(`Push "${pack.title}" to Amazon as a draft parent listing + ${variations?.count || 0} variation SKUs (${count} total)? This creates listings on your Amazon Seller account.`)) {
      return;
    }
    try {
      setPublishing(true);
      const overrides = {
        image_urls: pack.image_urls,
        image_base: (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''),
      };
      const res = await amazonAPI.publish(productId, overrides);
      toast.success(`Push started — ${res.data.summary?.total_skus} SKUs`);
      await load({ preserveEdits: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val || '');
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast.success('Copied');
  };

  if (loading || !pack) {
    return <div className="flex justify-center items-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }

  const titleLen = (pack.title || '').length;
  const stBytes = byteLen(pack.search_terms);
  const push = pack.push;

  return (
    <div className="space-y-6 pb-8" data-testid="amazon-panel">
      {/* ===== Status banner ===== */}
      <div className={`rounded-xl border px-5 py-4 ${status?.configured ? (status?.token_ok ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200') : 'bg-amber-50 border-amber-200'}`}>
        <div className="text-sm font-semibold flex items-center gap-2">
          <ShoppingCart size={14} /> Amazon Seller API (SP-API)
          {status?.configured
            ? (status?.token_ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-red-600" />)
            : <AlertCircle size={14} className="text-amber-600" />}
        </div>
        <div className="text-[11px] text-zinc-600 mt-0.5" data-testid="amazon-status-line">
          {status?.configured
            ? (status?.token_ok
              ? `Connected · marketplace ${status.marketplace_id} · seller ${status.seller_id}`
              : status?.token_error || 'Token exchange failed — check the LWA credentials.')
            : 'Not connected yet — everything below still works (generate, edit, preview, copy). To enable "Push to Amazon", add AMAZON_LWA_CLIENT_ID, AMAZON_LWA_CLIENT_SECRET, AMAZON_REFRESH_TOKEN and AMAZON_SELLER_ID to the backend env.'}
        </div>
      </div>

      {/* ===== AI generate ===== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Amazon SEO listing</h3>
          <p className="text-[11px] text-zinc-500 max-w-xl mt-0.5">
            Generates a keyword-front-loaded title (≤75 chars — Amazon&apos;s new limit effective Jul 27, 2026),
            5 benefit bullets, description, backend search terms and a spec sheet — from this product&apos;s approved Etsy data.
          </p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating || genRunning}
          size="sm"
          className="bg-zinc-900 hover:bg-zinc-800 text-white h-9 px-4 text-xs"
          data-testid="amazon-generate-btn"
        >
          {(generating || genRunning)
            ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Generating…</>
            : <><Sparkles size={12} className="mr-1.5" /> {pack.title ? 'Regenerate with AI' : 'Generate with AI'}</>}
        </Button>
      </div>
      {pack.generation?.status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[12px] text-red-700" data-testid="amazon-generation-error">
          Last generation failed: {pack.generation.error}
        </div>
      )}

      {/* ===== Listing form ===== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="text-sm font-semibold text-zinc-900">Listing content</h3>
          <div className="flex items-center gap-1.5">
            <Button onClick={handlePreview} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="amazon-preview-btn">
              <Eye size={12} className="mr-1.5" /> Preview SP-API JSON
            </Button>
            {status?.token_ok && (
              <Button onClick={handleValidate} disabled={validating} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="amazon-validate-btn">
                {validating ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <CheckCircle2 size={12} className="mr-1.5" />}
                Validate with Amazon
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} variant="outline" size="sm" className="h-8 px-3 text-xs" data-testid="amazon-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
              Save draft
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing || pushRunning || !status?.token_ok}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
              data-testid="amazon-publish-btn"
              title={!status?.token_ok ? 'Amazon SP-API not connected yet' : ''}
            >
              {publishing ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />}
              Push to Amazon
            </Button>
          </div>
        </div>

        {/* Title */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">SEO Title</Label>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-mono ${titleLen > 75 ? 'text-red-600 font-bold' : 'text-emerald-600'}`} data-testid="amazon-title-count">
                {titleLen}/75
              </span>
              <button onClick={() => copy(pack.title, 'title')} className="text-zinc-400 hover:text-zinc-900" data-testid="amazon-title-copy">
                {copiedKey === 'title' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
              </button>
            </div>
          </div>
          <Input
            value={pack.title || ''}
            onChange={(e) => setField('title', e.target.value)}
            className="mt-1 h-9 text-[13px]"
            data-testid="amazon-title"
          />
          {titleLen > 75 && (
            <div className="text-[10px] text-red-600 mt-1">Over Amazon&apos;s 75-character limit (enforced from Jul 27, 2026) — Amazon may auto-rewrite it.</div>
          )}
        </div>

        {/* Bullets */}
        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
            Bullet points <span className="normal-case font-normal tracking-normal text-zinc-400">(5 · ALL-CAPS lead-in · ≤200 chars each)</span>
          </Label>
          <div className="mt-1 space-y-2">
            {(pack.bullet_points || ['', '', '', '', '']).map((b, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[10px] font-mono text-zinc-400 mt-2.5 w-4">{i + 1}.</span>
                <textarea
                  value={b}
                  onChange={(e) => setBullet(i, e.target.value)}
                  rows={2}
                  className="flex-1 text-[12px] bg-white border border-zinc-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-amber-200"
                  data-testid={`amazon-bullet-${i}`}
                />
                <span className={`text-[9px] font-mono mt-2.5 ${b.length > 200 ? 'text-red-600' : 'text-zinc-400'}`}>{b.length}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Description + Search terms */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Product description</Label>
              <span className="text-[10px] font-mono text-zinc-400">{(pack.description || '').length}/2000</span>
            </div>
            <textarea
              value={pack.description || ''}
              onChange={(e) => setField('description', e.target.value)}
              rows={9}
              maxLength={2000}
              className="mt-1 w-full text-[12px] bg-white border border-zinc-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="amazon-description"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                Backend search terms <span className="normal-case font-normal tracking-normal text-zinc-400">(hidden keywords)</span>
              </Label>
              <span className={`text-[10px] font-mono ${stBytes > 249 ? 'text-red-600 font-bold' : 'text-emerald-600'}`} data-testid="amazon-search-terms-bytes">
                {stBytes}/249 bytes
              </span>
            </div>
            <textarea
              value={pack.search_terms || ''}
              onChange={(e) => setField('search_terms', e.target.value)}
              rows={4}
              className="mt-1 w-full text-[12px] bg-white border border-zinc-200 rounded-md p-2 font-mono focus:outline-none focus:ring-2 focus:ring-amber-200"
              data-testid="amazon-search-terms"
            />
            <div className="text-[10px] text-zinc-400 mt-1">
              Lowercase, space-separated, no commas, don&apos;t repeat title words.
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <Field label="SKU base" value={pack.sku_base || ''} onChange={(v) => setField('sku_base', v)} testid="amazon-sku-base" />
              <Field label="Brand" value={pack.brand || ''} onChange={(v) => setField('brand', v)} testid="amazon-brand" />
              <Field label="Quantity / SKU" value={pack.quantity} onChange={(v) => setField('quantity', v)} testid="amazon-quantity" type="number" />
              <Field label="Lead time (days)" value={pack.lead_time_days} onChange={(v) => setField('lead_time_days', v)} testid="amazon-lead-time" type="number" hint="Made-to-order handling time" />
              <Field label="Ring size (default)" value={pack.ring_size || ''} onChange={(v) => setField('ring_size', v)} testid="amazon-ring-size" />
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Amazon product type</Label>
                <select
                  value={pack.amazon_product_type || 'RING'}
                  onChange={(e) => setField('amazon_product_type', e.target.value)}
                  className="mt-1 h-9 w-full text-[13px] bg-white border border-zinc-200 rounded-md px-2"
                  data-testid="amazon-product-type"
                >
                  {['RING', 'NECKLACE', 'EARRING', 'BRACELET'].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Resizable</Label>
                <select
                  value={pack.resizable ? 'yes' : 'no'}
                  onChange={(e) => setField('resizable', e.target.value === 'yes')}
                  className="mt-1 h-9 w-full text-[13px] bg-white border border-zinc-200 rounded-md px-2"
                  data-testid="amazon-resizable"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">GTIN exemption</Label>
                <select
                  value={pack.gtin_exemption ? 'yes' : 'no'}
                  onChange={(e) => setField('gtin_exemption', e.target.value === 'yes')}
                  className="mt-1 h-9 w-full text-[13px] bg-white border border-zinc-200 rounded-md px-2"
                  data-testid="amazon-gtin-exemption"
                >
                  <option value="yes">Yes — no UPC needed</option>
                  <option value="no">No — items have UPCs</option>
                </select>
                <div className="text-[10px] text-zinc-400 mt-1">Requires an approved GTIN exemption in Seller Central for your brand.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Specifications</Label>
          {Object.keys(pack.specifications || {}).length === 0 ? (
            <div className="text-[11px] text-zinc-400 mt-2">No specs yet — hit &quot;Generate with AI&quot; to build the spec sheet.</div>
          ) : (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="amazon-specs">
              {Object.entries(pack.specifications || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-[12px]">
                  <span className="w-44 flex-shrink-0 text-zinc-500 truncate" title={k}>{k}</span>
                  <Input
                    value={v}
                    onChange={(e) => setSpec(k, e.target.value)}
                    className="h-7 text-[12px] flex-1"
                    data-testid={`amazon-spec-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  />
                  <button onClick={() => removeSpec(k)} className="text-zinc-300 hover:text-red-500" title="Remove row">
                    <XCircle size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image picker */}
        <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="amazon-image-selector">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 inline-flex items-center gap-1.5">
                <ImageIcon size={11} /> Listing photos
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                First selected = main image. Amazon wants ≥1000px, product on white filling ~85% of frame.
              </div>
            </div>
            <span className="text-[11px] font-mono px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-100" data-testid="amazon-image-count">
              {(pack.image_urls || []).length} selected
            </span>
          </div>
          {(pack.available_images || []).length === 0 ? (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              No product photos found. Upload images on the Etsy tab first — they&apos;ll appear here automatically.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="amazon-image-grid">
              {(pack.available_images || []).map((img) => {
                const selectedIdx = (pack.image_urls || []).indexOf(img.url);
                const isSel = selectedIdx >= 0;
                return (
                  <button
                    key={img.id || img.url}
                    type="button"
                    onClick={() => {
                      const current = pack.image_urls || [];
                      const next = isSel ? current.filter((u) => u !== img.url) : [...current, img.url];
                      setField('image_urls', next);
                    }}
                    className={`relative aspect-square rounded overflow-hidden border-2 transition focus:outline-none ${
                      isSel ? 'border-amber-500 ring-2 ring-amber-200' : 'border-zinc-200 hover:border-zinc-400'
                    }`}
                    title={img.label || ''}
                    data-testid={`amazon-image-thumb-${img.id || img.url}`}
                  >
                    <img src={img.url} alt={img.label || 'product'} className="w-full h-full object-cover bg-zinc-100" loading="lazy" />
                    {isSel && (
                      <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">
                        {selectedIdx + 1}
                      </span>
                    )}
                    {isSel && selectedIdx === 0 && (
                      <span className="absolute bottom-1 left-1 bg-white/95 text-amber-700 text-[9px] font-semibold px-1.5 py-0.5 rounded">MAIN</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ===== Variation matrix ===== */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="amazon-variations-card">
        <button
          className="w-full flex items-center justify-between"
          onClick={() => setShowMatrix((s) => !s)}
          data-testid="amazon-variations-toggle"
        >
          <div className="text-left">
            <h3 className="text-sm font-semibold text-zinc-900">
              Variations — same grid as Etsy · {variations?.count || 0} SKUs
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Metal × center-stone carat, priced with Amazon&apos;s jewelry fee (20% of first $250 + 5% above).
              {variations ? ` Range ${`$${variations.min_price?.toLocaleString()} – $${variations.max_price?.toLocaleString()}`} · theme ${variations.variation_theme}` : ''}
            </p>
          </div>
          {showMatrix ? <ChevronUp size={15} className="text-zinc-400" /> : <ChevronDown size={15} className="text-zinc-400" />}
        </button>
        {showMatrix && variations && <VariationMatrix rows={variations.rows} />}
      </div>

      {/* ===== Validation dry-run results ===== */}
      {validateResults && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="amazon-validate-card">
          <h3 className="text-sm font-semibold text-zinc-900">Amazon validation dry-run <span className="text-[10px] font-normal text-zinc-400">(nothing was created)</span></h3>
          <div className="mt-2 space-y-2">
            {validateResults.results.map((r) => (
              <div key={r.sku} className="text-[11px] border border-zinc-100 rounded p-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono">{r.sku}</span>
                  <span className="text-zinc-400">{r.label}</span>
                  <span className={`ml-auto font-semibold ${(r.status === 'ACCEPTED' || r.status === 'VALID') ? 'text-emerald-600' : 'text-red-600'}`}>{r.status}</span>
                </div>
                {(r.issues || []).map((iss, i) => (
                  <div key={i} className={`mt-1 ${iss.severity === 'ERROR' ? 'text-red-600' : 'text-amber-600'}`}>
                    [{iss.severity}] {iss.code ? `${iss.code}: ` : ''}{iss.message}
                    {iss.attributes?.length > 0 && <span className="text-zinc-400"> ({iss.attributes.join(', ')})</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Push status ===== */}
      {push && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="amazon-push-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
              Last push
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${PUSH_STATUS_BADGE[push.status] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>
                {push.status === 'running' ? `pushing ${push.done || 0}/${push.total}` : push.status?.replace(/_/g, ' ')}
              </span>
            </h3>
            <div className="text-[11px] text-zinc-500">
              {push.started_at ? `started ${push.started_at.slice(0, 19)}Z` : ''}
            </div>
          </div>
          {push.status === 'running' && (
            <div className="text-[11px] text-zinc-500 italic mt-2 flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> Pushing SKUs to Amazon — you can leave this page, progress is saved.
            </div>
          )}
          {(push.results || []).length > 0 && (
            <div className="mt-3 max-h-72 overflow-auto border border-zinc-100 rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left text-zinc-500">
                    <th className="px-2 py-1.5 font-medium">SKU</th>
                    <th className="px-2 py-1.5 font-medium">Variation</th>
                    <th className="px-2 py-1.5 font-medium">Status</th>
                    <th className="px-2 py-1.5 font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {push.results.map((r) => (
                    <tr key={r.sku} className="border-t border-zinc-100 align-top">
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.sku}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.label}</td>
                      <td className={`px-2 py-1.5 font-semibold whitespace-nowrap ${r.status === 'ACCEPTED' ? 'text-emerald-600' : 'text-red-600'}`}>{r.status}</td>
                      <td className="px-2 py-1.5 text-zinc-600">
                        {(r.issues || []).map((iss, i) => (
                          <div key={i}>{iss.code ? `${iss.code}: ` : ''}{iss.message}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===== Preview JSON modal ===== */}
      <Dialog open={!!previewJson} onOpenChange={() => setPreviewJson(null)}>
        <DialogContent className="sm:max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle>Amazon SP-API payload preview</DialogTitle>
            <DialogDescription className="sr-only">
              Dry-run of the JSON payloads that will be sent to the Amazon Listings Items API.
            </DialogDescription>
          </DialogHeader>
          {previewJson && (
            <>
              <div className="bg-zinc-50 rounded p-3 text-[12px] font-mono grid grid-cols-2 gap-x-3 gap-y-1">
                {Object.entries(previewJson.summary || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 col-span-1">
                    <span className="text-zinc-500">{k}</span>
                    <span className="text-zinc-900 truncate">{String(v)}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2 mb-1 flex justify-between items-center">
                <span>Parent payload (PUT /listings/2021-08-01/items/…)</span>
                <button
                  onClick={() => copy(JSON.stringify(previewJson.parent_payload, null, 2), 'json')}
                  className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900"
                >
                  {copiedKey === 'json' ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />} Copy
                </button>
              </div>
              <pre className="bg-zinc-900 text-amber-200 text-[10px] font-mono rounded p-3 overflow-auto max-h-[30vh] leading-snug">
                {JSON.stringify(previewJson.parent_payload, null, 2)}
              </pre>
              {previewJson.sample_child_payload && (
                <>
                  <div className="text-[11px] text-zinc-500 mt-2 mb-1">Sample child payload (1 of {(previewJson.skus || []).length - 1})</div>
                  <pre className="bg-zinc-900 text-emerald-300 text-[10px] font-mono rounded p-3 overflow-auto max-h-[25vh] leading-snug">
                    {JSON.stringify(previewJson.sample_child_payload, null, 2)}
                  </pre>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VariationMatrix({ rows }) {
  const carats = [...new Set(rows.map((r) => r.carat_label).filter(Boolean))];
  const combos = [];
  const seen = new Set();
  rows.forEach((r) => {
    const key = `${r.metal}|${r.metal_purity}`;
    if (!seen.has(key)) { seen.add(key); combos.push({ metal: r.metal, purity: r.metal_purity }); }
  });
  const priceFor = (combo, caratLabel) => {
    const row = rows.find((r) => r.metal === combo.metal && r.metal_purity === combo.purity && (caratLabel ? r.carat_label === caratLabel : true));
    return row ? `$${row.price.toLocaleString()}` : '—';
  };
  return (
    <div className="mt-3 overflow-x-auto" data-testid="amazon-variation-matrix">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-zinc-500 border-b border-zinc-200">
            <th className="px-2 py-1.5 font-medium whitespace-nowrap">Metal</th>
            {carats.length > 0
              ? carats.map((c) => <th key={c} className="px-2 py-1.5 font-medium text-right whitespace-nowrap">{c}</th>)
              : <th className="px-2 py-1.5 font-medium text-right">Price</th>}
          </tr>
        </thead>
        <tbody>
          {combos.map((combo) => (
            <tr key={`${combo.metal}-${combo.purity}`} className="border-b border-zinc-50">
              <td className="px-2 py-1.5 whitespace-nowrap font-medium text-zinc-700">
                {combo.metal} <span className="text-zinc-400">{combo.purity}</span>
              </td>
              {carats.length > 0
                ? carats.map((c) => <td key={c} className="px-2 py-1.5 text-right font-mono">{priceFor(combo, c)}</td>)
                : <td className="px-2 py-1.5 text-right font-mono">{priceFor(combo, null)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function Field({ label, value, onChange, testid, type = 'text', hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 text-[13px]"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}
